import { useEffect, useState } from "react";
import api, { fileUrl } from "../../lib/api";
import { Plus, Trash2, Pencil, Save, X, Upload as UploadIcon, Copy } from "lucide-react";

const empty = { title: "", description: "", category: "Report", file_url: "", image_url: "", year: String(new Date().getFullYear()), published: true };

export default function ResourcesEditor() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/resources?all=true").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const save = async (r) => {
        if (r.id) await api.put(`/resources/${r.id}`, r); else await api.post("/resources", r);
        setEditing(null); load();
    };
    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/resources/${id}`); load(); };
    const dup = async (id) => { await api.post(`/resources/${id}/duplicate`); load(); };

    return (
        <div data-testid="resources-editor">
            <div className="flex items-center justify-between mb-8">
                <div><h1 className="font-display text-4xl mb-2">Resources & Publications</h1><p className="text-muted-foreground">Reports, strategic plans, case studies and policy briefs.</p></div>
                <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium" data-testid="add-resource"><Plus size={14} /> New Resource</button>
            </div>
            <div className="bg-card border border-border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        <tr><th className="text-left p-4">Title</th><th className="text-left p-4">Category</th><th className="text-left p-4">Year</th><th className="text-right p-4">Actions</th></tr>
                    </thead>
                    <tbody>
                        {items.map((r) => (
                            <tr key={r.id} className="border-t border-border">
                                <td className="p-4 font-medium">{r.title}</td>
                                <td className="p-4 text-muted-foreground">{r.category}</td>
                                <td className="p-4 text-muted-foreground">{r.year}</td>
                                <td className="p-4 text-right space-x-2">
                                    <button onClick={() => setEditing({ ...r })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs"><Pencil size={12} /> Edit</button>
                                    <button onClick={() => dup(r.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs" data-testid={`dup-res-${r.id}`}><Copy size={12} /> Duplicate</button>
                                    <button onClick={() => del(r.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-destructive text-destructive text-xs"><Trash2 size={12} /> Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {editing && <Modal item={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />}
        </div>
    );
}

const Modal = ({ item, onChange, onSave, onClose }) => {
    const set = (k, v) => onChange({ ...item, [k]: v });
    const upload = async (file, key) => {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        set(key, data.url);
    };
    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-start justify-center p-6 overflow-y-auto">
            <div className="bg-card border border-border rounded-sm w-full max-w-3xl my-10">
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="font-display text-2xl">{item.id ? "Edit Resource" : "New Resource"}</h2>
                    <button onClick={onClose} className="p-2 rounded-sm hover:bg-muted"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <F label="Title" value={item.title} onChange={(v) => set("title", v)} testid="res-title" />
                    <T label="Description" value={item.description} onChange={(v) => set("description", v)} testid="res-desc" />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <F label="Category" value={item.category} onChange={(v) => set("category", v)} testid="res-cat" />
                        <F label="Year" value={item.year} onChange={(v) => set("year", v)} testid="res-year" />
                    </div>
                    <Uploader label="Cover image" value={item.image_url} onChange={(v) => set("image_url", v)} onUpload={(f) => upload(f, "image_url")} accept="image/*" />
                    <Uploader label="Downloadable file (PDF)" value={item.file_url} onChange={(v) => set("file_url", v)} onUpload={(f) => upload(f, "file_url")} accept="application/pdf" />
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!item.published} onChange={(e) => set("published", e.target.checked)} /><span className="text-sm">Published</span></label>
                </div>
                <div className="p-6 border-t border-border flex justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 rounded-sm border border-border text-sm">Cancel</button>
                    <button onClick={() => onSave(item)} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium"><Save size={14} /> Save</button>
                </div>
            </div>
        </div>
    );
};

const Uploader = ({ label, value, onChange, onUpload, accept }) => (
    <div>
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <div className="flex items-center gap-3">
            <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 px-3 py-2.5 rounded-sm border border-border bg-background text-sm" />
            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-border cursor-pointer text-sm"><UploadIcon size={14} /> Upload
                <input type="file" className="hidden" accept={accept} onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} />
            </label>
        </div>
        {value && accept.includes("image") && <img src={fileUrl(value)} alt="" className="mt-3 h-28 rounded-sm border border-border object-cover" />}
    </div>
);
const F = ({ label, value, onChange, testid }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" /></label>
);
const T = ({ label, value, onChange, testid }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm resize-y" /></label>
);
